import HBIcon from "@/components/HBIcon";
import { TrackerA } from "@/components/Tracker";
import { AB_FEEDBACK_SEARCH } from "@/constants/ABMng";
import useAppStore from "@/context/store";
import { shieldPin } from "@/services";
import { canEditPinFn } from "@/utils/board";
import { getContentType } from "@/utils/getContentType";
import useABTest from "@/utils/hooks/useABtest";
import {
  SearchFeedbackTypeEnum,
  useFeedbackSearchStore,
} from "@/utils/hooks/useFeedbackSearch";
import imageUrl from "@/utils/imageUrl";
import { useMemoizedFn } from "ahooks";
import { Dropdown, type DropDownProps } from "antd";
import classNames from "classnames";
import { useCallback, useMemo, useState } from "react";
import { useLogin } from "../LoginModal";
import styles from "./index.module.less";

interface PurePinMoreProps {
  pin: IPin;
  onShield?: (pin: IPin) => void;
  onReport?: () => void;
  overideClassName?: string;
  onEdit?: (pin: IPin) => void;
  onDelete?: (pin: IPin) => void;
  /** 是否是全屏 */
  isFull?: boolean;
  moduleName?: string;
  searchWord?: string;
  trackerData?: Record<string, any>;
  placement?: DropDownProps["placement"];
  /** 是否在搜索结果页 */
  isSearchPage?: boolean;
}

export default function PurePinMore({
  pin,
  onShield,
  onReport,
  overideClassName,
  onEdit,
  onDelete,
  isFull = false,
  moduleName,
  searchWord,
  trackerData,
  placement,
  isSearchPage = false,
}: PurePinMoreProps) {
  const { login } = useLogin();
  const user = useAppStore(({ user }) => user);
  const contentType = useMemo(() => {
    return getContentType(pin);
  }, [pin]);

  // 渐进式搜索 AB 测试
  const feedbackSearchAB = useABTest(AB_FEEDBACK_SEARCH);
  const showFeedbackOptions = isSearchPage && feedbackSearchAB.new;

  // 获取反馈状态
  const { feedbacks, addFeedback, triggerSearch } = useFeedbackSearchStore();
  const currentFeedback = useMemo(() => {
    return feedbacks.find((f) => f.fileId === pin?.file_id);
  }, [feedbacks, pin?.file_id]);

  const [shieldShow, setShieldShow] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const onShieldFn = useMemoizedFn(async (e) => {
    e.stopPropagation();
    setShieldShow(true);
  });

  // 喜欢/不喜欢操作
  const onFeedback = useMemoizedFn(
    (feedbackType: SearchFeedbackTypeEnum, e: React.MouseEvent) => {
      e.stopPropagation();
      const thumbnailUrl = imageUrl(pin?.file, { width: 120 });
      addFeedback({
        fileId: pin?.file_id,
        feedbackType,
        thumbnailUrl,
      });
      triggerSearch();
      setDropdownOpen(false);
    }
  );

  /** 屏蔽原因 */
  const onShieldReason = useCallback(
    async (reason) => {
      const result = await shieldPin({
        item_type: 1,
        item_value: pin?.file_id,
        reason,
      });

      if (!result.err) {
        // 在 pin 对象中添加屏蔽原因字段
        const pinWithShieldReason = {
          ...pin,
          shield_reason: reason,
        };
        onShield?.(pinWithShieldReason);
        setShieldShow(false);
        setDropdownOpen(false);
      }
    },
    [pin, onShield]
  );

  const isMe = user?.user_id === pin?.user_id;

  const canEdit = useMemo(() => {
    return canEditPinFn(pin, user);
  }, [pin, user]);

  const moreMenu = useMemo(() => {
    if (!user) {
      return <div></div>;
    }
    return (
      <div className={styles.menu} data-content-type={contentType}>
        {/* 渐进式搜索：感兴趣/不感兴趣选项 */}
        {showFeedbackOptions && (
          <>
            <TrackerA
              className={classNames(styles.menuItem, {
                [styles.menuItemActive]:
                  currentFeedback?.feedbackType ===
                  SearchFeedbackTypeEnum.POSITIVE,
              })}
              onClick={(e) => onFeedback(SearchFeedbackTypeEnum.POSITIVE, e)}
              data-gd-click="button_click"
              data-button-name="感兴趣"
              data-content-id={pin?.pin_id}
              data-board-id={pin?.board_id}
              data-file-id={pin?.file_id}
              data-search-word={searchWord}
              data-content-type={contentType}
              {...(moduleName ? { "data-module-name": moduleName } : null)}
              {...trackerData}
            >
              <span className={styles.text}>感兴趣</span>
              {currentFeedback?.feedbackType ===
                SearchFeedbackTypeEnum.POSITIVE && (
                <HBIcon className={styles.checkIcon} type="ic_check" />
              )}
            </TrackerA>
            <TrackerA
              className={classNames(styles.menuItem, {
                [styles.menuItemActive]:
                  currentFeedback?.feedbackType ===
                  SearchFeedbackTypeEnum.NEGATIVE,
              })}
              onClick={(e) => onFeedback(SearchFeedbackTypeEnum.NEGATIVE, e)}
              data-gd-click="button_click"
              data-button-name="不感兴趣"
              data-content-id={pin?.pin_id}
              data-board-id={pin?.board_id}
              data-file-id={pin?.file_id}
              data-search-word={searchWord}
              data-content-type={contentType}
              {...(moduleName ? { "data-module-name": moduleName } : null)}
              {...trackerData}
            >
              <span className={styles.text}>不感兴趣</span>
              {currentFeedback?.feedbackType ===
                SearchFeedbackTypeEnum.NEGATIVE && (
                <HBIcon className={styles.checkIcon} type="ic_check" />
              )}
            </TrackerA>
            <div className={styles.menuDivider} />
          </>
        )}
        {!isMe && (
          <>
            <TrackerA
              className={styles.menuItem}
              onClick={onShieldFn}
              data-gd-click="button_click"
              data-button-name="屏蔽"
              data-report-type="采集"
              data-report-pin-id={pin?.pin_id}
              data-report-file-id={pin?.file_id}
              data-report-content-id={pin?.file_material?.material_id}
              data-material-id={pin?.file_material?.material_id}
              data-board-id={pin?.board_id}
              data-file-id={pin?.file_id}
              data-content-id={pin?.pin_id}
              data-search-word={searchWord}
              data-ext={JSON.stringify(pin?.ext)}
              {...(moduleName ? { "data-module-name": moduleName } : null)}
              {...trackerData}
              data-location="1"
            >
              <span className={styles.text}>屏蔽采集</span>
            </TrackerA>
            <TrackerA
              className={styles.menuItem}
              onClick={() => {
                setDropdownOpen(false);
                onReport?.();
              }}
              data-gd-click="button_click"
              data-button-name="举报"
              data-report-type="采集"
              data-report-pin-id={pin?.pin_id}
              data-report-file-id={pin?.file_id}
              data-report-content-id={pin?.file_material?.material_id}
              data-material-id={pin?.file_material?.material_id}
              data-board-id={pin?.board_id}
              data-file-id={pin?.file_id}
              data-content-id={pin?.pin_id}
              data-search-word={searchWord}
              data-ext={JSON.stringify(pin?.ext)}
              {...(moduleName ? { "data-module-name": moduleName } : null)}
              {...trackerData}
              data-location="2"
            >
              <span className={styles.text}>举报采集</span>
            </TrackerA>
          </>
        )}
        {canEdit && (
          <>
            <TrackerA
              className={styles.menuItem}
              onClick={() => {
                setDropdownOpen(false);
                onEdit?.(pin);
              }}
              data-gd-click="button_click"
              data-button-name="编辑"
              data-content-id={pin?.pin_id}
              data-board-id={pin?.board_id}
              data-file-id={pin?.file_id}
              data-material-id={
                pin?.file_material?.material_id || pin?.extra?.material_id
              }
              data-search-word={searchWord}
              data-content-type={contentType}
              {...(moduleName ? { "data-module-name": moduleName } : null)}
              {...trackerData}
            >
              <span className={styles.text}>编辑采集</span>
            </TrackerA>
            <TrackerA
              className={styles.menuItem}
              onClick={() => {
                setDropdownOpen(false);
                onDelete?.(pin);
              }}
              data-gd-click="button_click"
              data-button-name="删除采集"
              data-content-id={pin?.pin_id}
              data-board-id={pin?.board_id}
              data-file-id={pin?.file_id}
              data-material-id={
                pin?.file_material?.material_id || pin?.extra?.material_id
              }
              data-search-word={searchWord}
              data-content-type={contentType}
              {...(moduleName ? { "data-module-name": moduleName } : null)}
              {...trackerData}
            >
              <span className={styles.text}>删除采集</span>
            </TrackerA>
          </>
        )}
      </div>
    );
  }, [
    user,
    contentType,
    onShieldFn,
    pin,
    onReport,
    isMe,
    onEdit,
    onDelete,
    moduleName,
    searchWord,
    trackerData,
    showFeedbackOptions,
    currentFeedback,
    onFeedback,
    canEdit,
  ]);

  const shieldMenu = useMemo(() => {
    if (!shieldShow) {
      return null;
    }

    // 不感兴趣,看到了太多次,图片模糊,不够美观,内容引人不适,其他
    const shieldReasonList = [
      "内容不感兴趣",
      "看到了太多次",
      "图片模糊",
      "不够美观",
      "内容引人不适",
    ];

    return (
      <div className={classNames(styles.menu, styles.shieldMenu)}>
        <div className={styles.desc}>
          请告诉我们原因，我们将为你优化推荐结果：
        </div>
        {shieldReasonList.map((item, index) => (
          <TrackerA
            key={item}
            className={styles.menuItem}
            onClick={(e) => {
              onShieldReason(item);
            }}
            data-gd-click="button_click"
            data-button-name="屏蔽原因"
            data-report-reason={item}
            data-content-id={pin?.pin_id}
            data-board-id={pin?.board_id}
            data-file-id={pin?.file_id}
            data-material-id={pin?.file_material?.material_id}
            data-unique-id={pin?.pin_id}
            data-location={index + 1}
            data-content-type={contentType}
            data-search-word={searchWord}
            data-ext={JSON.stringify(pin?.ext)}
            {...(moduleName ? { "data-module-name": moduleName } : null)}
            {...trackerData}
          >
            <span className={styles.text}>{item}</span>
          </TrackerA>
        ))}
      </div>
    );
  }, [
    onShieldReason,
    pin,
    shieldShow,
    contentType,
    moduleName,
    searchWord,
    trackerData,
  ]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!user) {
        login({
          source_location: "采集卡片更多操作",
        });
        return;
      }
      setDropdownOpen(true);
    },
    [login, user]
  );

  return (
    <Dropdown
      dropdownRender={() => shieldMenu || moreMenu}
      placement={placement || "bottomLeft"}
      open={dropdownOpen}
      trigger={["click"]}
      onOpenChange={(open) => {
        setDropdownOpen(open);
        if (!open) {
          setShieldShow(false);
        }
      }}
    >
      <div
        className={classNames(overideClassName || styles.more)}
        title="更多操作"
        onClick={onClick}
        data-gd-click="button_click"
        data-button-name="更多操作"
        data-content-id={pin?.pin_id}
        data-board-id={pin?.board_id}
        data-file-id={pin?.file_id}
        data-content-type={contentType}
        data-search-word={searchWord}
        {...(moduleName ? { "data-module-name": moduleName } : null)}
        {...trackerData}
      >
        <HBIcon className={styles.icon} type={isFull ? "hb_more" : "ic_more"} />
      </div>
    </Dropdown>
  );
}
